/**
 * AvailabilityGrid — PB-style visual weekly availability editor.
 * Reuses the same grid pattern as CoachCalendarView but for
 * managing availability windows instead of appointments.
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Modal } from '../admin/Modal';

interface AvailabilityWindow {
  id: string;
  coach_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  services: string[];
  is_active: boolean;
}

interface Service {
  id: string;
  name: string;
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PX_PER_HOUR = 60;
const START_HOUR = 5;
const END_HOUR = 22;
const TOTAL_HOURS = END_HOUR - START_HOUR;

// Generate time labels
const TIME_LABELS = Array.from({ length: TOTAL_HOURS * 2 }, (_, i) => {
  const hour = START_HOUR + Math.floor(i / 2);
  const isHalf = i % 2 === 1;
  return {
    hour,
    isHalf,
    label: isHalf
      ? `${hour > 12 ? hour - 12 : hour === 0 ? 12 : hour}:30`
      : hour === 0 ? '12 AM' : hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`
  };
});

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
};

// Convert HH:MM UTC to local hours using today's date for DST
function utcTimeToLocalHour(utcTime: string, tz: string): number {
  const [h, m] = utcTime.split(':').map(Number);
  const today = new Date();
  const d = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), h, m, 0));
  const local = new Date(d.toLocaleString('en-US', { timeZone: tz }));
  return local.getHours() + local.getMinutes() / 60;
}

// Convert local hour to HH:MM UTC
function localHourToUtcTime(localHour: number, tz: string): string {
  const today = new Date();
  const h = Math.floor(localHour);
  const m = Math.round((localHour - h) * 60);
  const local = new Date(today.getFullYear(), today.getMonth(), today.getDate(), h, m, 0);
  const tzOffset = local.getTime() - new Date(local.toLocaleString('en-US', { timeZone: tz })).getTime();
  const utc = new Date(local.getTime() + tzOffset);
  return `${String(utc.getUTCHours()).padStart(2, '0')}:${String(utc.getUTCMinutes()).padStart(2, '0')}`;
}

function formatLocalTime(hour: number): string {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

interface BlockEntry {
  id: string;
  coach_id: string;
  recurring?: boolean;
  day_of_week?: number;
  start_time: string;
  end_time: string;
  reason: string;
  type: string;
  all_day: boolean;
}

type ModalMode = 'availability' | 'block';

interface AvailabilityGridProps {
  windows: AvailabilityWindow[];
  blocks: BlockEntry[];
  services: Service[];
  onRefresh: () => void;
}

export function AvailabilityGrid({ windows, blocks, services, onRefresh }: AvailabilityGridProps) {
  const { user } = useAuth();
  const coachId = user?.id || '';
  const coachTZ = user?.timezone || 'America/New_York';

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('availability');
  const [editingWindow, setEditingWindow] = useState<AvailabilityWindow | null>(null);
  const [editingBlock, setEditingBlock] = useState<BlockEntry | null>(null);
  const [modalForm, setModalForm] = useState({ days: [1] as number[], startHour: 9, endHour: 12, services: [] as string[], reason: '' });
  const [error, setError] = useState<string | null>(null);

  // Drag-to-create state
  const [creating, setCreating] = useState<{ day: number; startHour: number; currentHour: number } | null>(null);
  const columnRefs = useRef<(HTMLDivElement | null)[]>([]);
  const gridRef = useRef<HTMLDivElement>(null);

  // Resize state for existing windows
  const [resizing, setResizing] = useState<{
    window: AvailabilityWindow & { localStart: number; localEnd: number };
    edge: 'top' | 'bottom';
    startY: number;
    originalStart: number;
    originalEnd: number;
    currentStart: number;
    currentEnd: number;
  } | null>(null);

  // Convert windows to local time for rendering
  const localWindows = windows.filter(w => w.is_active).map(w => ({
    ...w,
    localStart: utcTimeToLocalHour(w.start_time, coachTZ),
    localEnd: utcTimeToLocalHour(w.end_time, coachTZ)
  }));

  // Convert recurring blocks to local time for rendering
  const localBlocks = blocks.filter(b => b.recurring && b.day_of_week !== undefined).map(b => ({
    ...b,
    day_of_week: b.day_of_week!,
    localStart: utcTimeToLocalHour(b.start_time, coachTZ),
    localEnd: utcTimeToLocalHour(b.end_time, coachTZ)
  }));

  // Resize handler for existing windows
  const handleResizeStart = (e: React.MouseEvent, w: AvailabilityWindow & { localStart: number; localEnd: number }, edge: 'top' | 'bottom') => {
    e.stopPropagation();
    e.preventDefault();
    setResizing({
      window: w, edge, startY: e.clientY,
      originalStart: w.localStart, originalEnd: w.localEnd,
      currentStart: w.localStart, currentEnd: w.localEnd
    });
  };

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaHours = (e.clientY - resizing.startY) / PX_PER_HOUR;
      const snapped = Math.round(deltaHours * 4) / 4;
      let newStart = resizing.originalStart;
      let newEnd = resizing.originalEnd;

      if (resizing.edge === 'top') {
        newStart = Math.max(START_HOUR, Math.min(resizing.originalStart + snapped, newEnd - 0.25));
      } else {
        newEnd = Math.max(newStart + 0.25, Math.min(resizing.originalEnd + snapped, END_HOUR));
      }
      setResizing(prev => prev ? { ...prev, currentStart: newStart, currentEnd: newEnd } : null);
    };

    const handleMouseUp = async () => {
      if (resizing && (resizing.currentStart !== resizing.originalStart || resizing.currentEnd !== resizing.originalEnd)) {
        const utcStart = localHourToUtcTime(resizing.currentStart, coachTZ);
        const utcEnd = localHourToUtcTime(resizing.currentEnd, coachTZ);
        try {
          await fetch(`/api/availability/${coachId}/windows/${resizing.window.id}`, {
            method: 'PUT', headers: getAuthHeaders(),
            body: JSON.stringify({ start_time: utcStart, end_time: utcEnd })
          });
          onRefresh();
        } catch { /* ignore */ }
      }
      setResizing(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [resizing, coachId, coachTZ, onRefresh]);

  // Drag-to-move state for existing windows
  const [moving, setMoving] = useState<{
    window: AvailabilityWindow & { localStart: number; localEnd: number };
    startY: number;
    startX: number;
    originalDay: number;
    originalStart: number;
    duration: number;
    currentDay: number;
    currentStart: number;
    isDragging: boolean;
  } | null>(null);
  const movePotentialRef = useRef<{ window: AvailabilityWindow & { localStart: number; localEnd: number }; startY: number; startX: number } | null>(null);

  const handleBlockMouseDown = (e: React.MouseEvent, w: AvailabilityWindow & { localStart: number; localEnd: number }) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    movePotentialRef.current = { window: w, startY: e.clientY, startX: e.clientX };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Check for drag threshold
      if (movePotentialRef.current && !moving) {
        const dx = Math.abs(e.clientX - movePotentialRef.current.startX);
        const dy = Math.abs(e.clientY - movePotentialRef.current.startY);
        if (dx > 5 || dy > 5) {
          const w = movePotentialRef.current.window;
          setMoving({
            window: w,
            startY: e.clientY, startX: e.clientX,
            originalDay: w.day_of_week, originalStart: w.localStart,
            duration: w.localEnd - w.localStart,
            currentDay: w.day_of_week, currentStart: w.localStart,
            isDragging: true
          });
        }
        return;
      }

      if (!moving?.isDragging) return;

      // Calculate new position
      const grid = gridRef.current;
      if (!grid) return;

      // Determine which day column we're over
      let newDay = moving.originalDay;
      for (let d = 0; d < 7; d++) {
        const col = columnRefs.current[d];
        if (col) {
          const rect = col.getBoundingClientRect();
          if (e.clientX >= rect.left && e.clientX <= rect.right) {
            newDay = d;
            break;
          }
        }
      }

      // Calculate new start hour using grid rect for scroll-aware positioning
      const col = columnRefs.current[newDay];
      if (col) {
        const gridRect = grid.getBoundingClientRect();
        const relY = e.clientY - gridRect.top + grid.scrollTop;
        const hour = START_HOUR + relY / PX_PER_HOUR;
        const snapped = Math.round(hour * 4) / 4;
        const clamped = Math.max(START_HOUR, Math.min(snapped, END_HOUR - moving.duration));
        setMoving(prev => prev ? { ...prev, currentDay: newDay, currentStart: clamped } : null);
      }
    };

    const handleMouseUp = async () => {
      if (moving?.isDragging && (moving.currentDay !== moving.originalDay || moving.currentStart !== moving.originalStart)) {
        const utcStart = localHourToUtcTime(moving.currentStart, coachTZ);
        const utcEnd = localHourToUtcTime(moving.currentStart + moving.duration, coachTZ);
        try {
          await fetch(`/api/availability/${coachId}/windows/${moving.window.id}`, {
            method: 'PUT', headers: getAuthHeaders(),
            body: JSON.stringify({ day_of_week: moving.currentDay, start_time: utcStart, end_time: utcEnd })
          });
          onRefresh();
        } catch { /* ignore */ }
      }
      movePotentialRef.current = null;
      setMoving(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [moving, coachId, coachTZ, onRefresh]);

  // Block resize state
  const [blockResizing, setBlockResizing] = useState<{
    block: BlockEntry & { localStart: number; localEnd: number };
    edge: 'top' | 'bottom';
    startY: number;
    originalStart: number;
    originalEnd: number;
    currentStart: number;
    currentEnd: number;
  } | null>(null);

  const handleBlockResizeStart = (e: React.MouseEvent, b: BlockEntry & { localStart: number; localEnd: number }, edge: 'top' | 'bottom') => {
    e.stopPropagation();
    e.preventDefault();
    setBlockResizing({
      block: b, edge, startY: e.clientY,
      originalStart: b.localStart, originalEnd: b.localEnd,
      currentStart: b.localStart, currentEnd: b.localEnd
    });
  };

  useEffect(() => {
    if (!blockResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaHours = (e.clientY - blockResizing.startY) / PX_PER_HOUR;
      const snapped = Math.round(deltaHours * 4) / 4;
      let newStart = blockResizing.originalStart;
      let newEnd = blockResizing.originalEnd;
      if (blockResizing.edge === 'top') {
        newStart = Math.max(START_HOUR, Math.min(blockResizing.originalStart + snapped, newEnd - 0.25));
      } else {
        newEnd = Math.max(newStart + 0.25, Math.min(blockResizing.originalEnd + snapped, END_HOUR));
      }
      setBlockResizing(prev => prev ? { ...prev, currentStart: newStart, currentEnd: newEnd } : null);
    };

    const handleMouseUp = async () => {
      if (blockResizing && (blockResizing.currentStart !== blockResizing.originalStart || blockResizing.currentEnd !== blockResizing.originalEnd)) {
        const utcStart = localHourToUtcTime(blockResizing.currentStart, coachTZ);
        const utcEnd = localHourToUtcTime(blockResizing.currentEnd, coachTZ);
        try {
          await fetch(`/api/blocked-time/${coachId}/${blockResizing.block.id}`, {
            method: 'PUT', headers: getAuthHeaders(),
            body: JSON.stringify({ start_time: utcStart, end_time: utcEnd, recurring: true, day_of_week: blockResizing.block.day_of_week })
          });
          onRefresh();
        } catch { /* ignore */ }
      }
      setBlockResizing(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [blockResizing, coachId, coachTZ, onRefresh]);

  // Block drag-to-move state
  const [blockMoving, setBlockMoving] = useState<{
    block: BlockEntry & { localStart: number; localEnd: number };
    startY: number; startX: number;
    originalDay: number; originalStart: number; duration: number;
    currentDay: number; currentStart: number; isDragging: boolean;
  } | null>(null);
  const blockMovePotentialRef = useRef<{ block: BlockEntry & { localStart: number; localEnd: number }; startY: number; startX: number } | null>(null);

  const handleBlockEntryMouseDown = (e: React.MouseEvent, b: BlockEntry & { localStart: number; localEnd: number }) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    blockMovePotentialRef.current = { block: b, startY: e.clientY, startX: e.clientX };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (blockMovePotentialRef.current && !blockMoving) {
        const dx = Math.abs(e.clientX - blockMovePotentialRef.current.startX);
        const dy = Math.abs(e.clientY - blockMovePotentialRef.current.startY);
        if (dx > 5 || dy > 5) {
          const b = blockMovePotentialRef.current.block;
          setBlockMoving({
            block: b, startY: e.clientY, startX: e.clientX,
            originalDay: b.day_of_week, originalStart: b.localStart,
            duration: b.localEnd - b.localStart,
            currentDay: b.day_of_week, currentStart: b.localStart,
            isDragging: true
          });
        }
        return;
      }

      if (!blockMoving?.isDragging) return;

      const grid = gridRef.current;
      if (!grid) return;

      let newDay = blockMoving.originalDay;
      for (let d = 0; d < 7; d++) {
        const col = columnRefs.current[d];
        if (col) {
          const rect = col.getBoundingClientRect();
          if (e.clientX >= rect.left && e.clientX <= rect.right) { newDay = d; break; }
        }
      }

      const col = columnRefs.current[newDay];
      if (col) {
        const gridRect = grid.getBoundingClientRect();
        const relY = e.clientY - gridRect.top + grid.scrollTop;
        const hour = START_HOUR + relY / PX_PER_HOUR;
        const snapped = Math.round(hour * 4) / 4;
        const clamped = Math.max(START_HOUR, Math.min(snapped, END_HOUR - blockMoving.duration));
        setBlockMoving(prev => prev ? { ...prev, currentDay: newDay, currentStart: clamped } : null);
      }
    };

    const handleMouseUp = async () => {
      blockMovePotentialRef.current = null;
      if (!blockMoving?.isDragging) { setBlockMoving(null); return; }
      if (blockMoving.currentDay !== blockMoving.originalDay || blockMoving.currentStart !== blockMoving.originalStart) {
        const utcStart = localHourToUtcTime(blockMoving.currentStart, coachTZ);
        const utcEnd = localHourToUtcTime(blockMoving.currentStart + blockMoving.duration, coachTZ);
        try {
          await fetch(`/api/blocked-time/${coachId}/${blockMoving.block.id}`, {
            method: 'PUT', headers: getAuthHeaders(),
            body: JSON.stringify({ day_of_week: blockMoving.currentDay, start_time: utcStart, end_time: utcEnd, recurring: true })
          });
          onRefresh();
        } catch { /* ignore */ }
      }
      setBlockMoving(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [blockMoving, coachId, coachTZ, onRefresh]);

  // Click on existing availability window — open edit modal
  const handleWindowClick = (w: AvailabilityWindow & { localStart: number; localEnd: number }) => {
    setEditingWindow(w);
    setEditingBlock(null);
    setModalMode('availability');
    setModalForm({
      days: [w.day_of_week],
      startHour: w.localStart,
      endHour: w.localEnd,
      services: [...w.services],
      reason: ''
    });
    setShowModal(true);
  };

  // Click on existing block — open edit modal in block mode
  const handleBlockClick = (b: BlockEntry & { localStart: number; localEnd: number }) => {
    setEditingBlock(b);
    setEditingWindow(null);
    setModalMode('block');
    setModalForm({
      days: [b.day_of_week],
      startHour: b.localStart,
      endHour: b.localEnd,
      services: [],
      reason: b.reason || ''
    });
    setShowModal(true);
  };

  // Drag to create
  const handleMouseDown = (e: React.MouseEvent, day: number) => {
    if (e.button !== 0 || resizing || moving) return;
    const col = columnRefs.current[day];
    const grid = gridRef.current;
    if (!col || !grid) return;
    const gridRect = grid.getBoundingClientRect();
    const relY = e.clientY - gridRect.top + grid.scrollTop;
    const hour = START_HOUR + relY / PX_PER_HOUR;
    const snapHour = Math.round(hour * 4) / 4; // 15 min snap
    setCreating({ day, startHour: snapHour, currentHour: snapHour });
  };

  useEffect(() => {
    if (!creating) return;

    const handleMouseMove = (e: MouseEvent) => {
      const grid = gridRef.current;
      if (!grid) return;
      const gridRect = grid.getBoundingClientRect();
      const relY = e.clientY - gridRect.top + grid.scrollTop;
      const hour = START_HOUR + relY / PX_PER_HOUR;
      const snapHour = Math.max(START_HOUR, Math.min(Math.round(hour * 4) / 4, END_HOUR));
      setCreating(prev => prev ? { ...prev, currentHour: snapHour } : null);
    };

    const handleMouseUp = () => {
      if (creating && Math.abs(creating.currentHour - creating.startHour) >= 0.25) {
        const start = Math.min(creating.startHour, creating.currentHour);
        const end = Math.max(creating.startHour, creating.currentHour);
        setEditingWindow(null);
        setEditingBlock(null);
        setModalMode('availability');
        setModalForm({
          days: [creating.day],
          startHour: start,
          endHour: end,
          services: [],
          reason: ''
        });
        setShowModal(true);
      }
      setCreating(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, [creating]);

  // Save (create or update)
  const handleSave = async () => {
    setError(null);
    try {
      const utcStart = localHourToUtcTime(modalForm.startHour, coachTZ);
      const utcEnd = localHourToUtcTime(modalForm.endHour, coachTZ);

      if (modalMode === 'block') {
        // Block mode — save to blocked time API
        if (editingBlock) {
          const res = await fetch(`/api/blocked-time/${coachId}/${editingBlock.id}`, {
            method: 'PUT', headers: getAuthHeaders(),
            body: JSON.stringify({ day_of_week: modalForm.days[0], start_time: utcStart, end_time: utcEnd, reason: modalForm.reason, recurring: true })
          });
          if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
        } else {
          for (const day of modalForm.days) {
            const res = await fetch(`/api/blocked-time/${coachId}`, {
              method: 'POST', headers: getAuthHeaders(),
              body: JSON.stringify({ day_of_week: day, start_time: utcStart, end_time: utcEnd, reason: modalForm.reason, type: 'blocked', recurring: true })
            });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
          }
        }
      } else {
        // Availability mode — save to availability API
        if (editingWindow) {
          const res = await fetch(`/api/availability/${coachId}/windows/${editingWindow.id}`, {
            method: 'PUT', headers: getAuthHeaders(),
            body: JSON.stringify({ day_of_week: modalForm.days[0], start_time: utcStart, end_time: utcEnd, services: modalForm.services })
          });
          if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
        } else {
          for (const day of modalForm.days) {
            const res = await fetch(`/api/availability/${coachId}/windows`, {
              method: 'POST', headers: getAuthHeaders(),
              body: JSON.stringify({ day_of_week: day, start_time: utcStart, end_time: utcEnd, services: modalForm.services })
            });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
          }
        }
      }
      setShowModal(false);
      onRefresh();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      if (modalMode === 'block' && editingBlock) {
        await fetch(`/api/blocked-time/${coachId}/${editingBlock.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      } else if (editingWindow) {
        await fetch(`/api/availability/${coachId}/windows/${editingWindow.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      }
      setShowModal(false);
      onRefresh();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const toggleService = (svcId: string) => {
    setModalForm(prev => ({
      ...prev,
      services: prev.services.includes(svcId) ? prev.services.filter(s => s !== svcId) : [...prev.services, svcId]
    }));
  };

  return (
    <div className="avail-grid-container">
      {/* Header */}
      <div className="avail-grid-header">
        <div className="avail-gutter-header" />
        {DAYS.map((day, i) => (
          <div key={i} className="avail-day-header">{DAY_ABBR[i]}</div>
        ))}
      </div>

      {/* Grid */}
      <div className="avail-grid" ref={gridRef}>
        {/* Time gutter */}
        <div className="avail-gutter">
          {TIME_LABELS.map((t, i) => (
            <div key={i} className={`avail-time-label ${t.isHalf ? 'half' : 'hour'}`}>{t.label}</div>
          ))}
        </div>

        {/* Day columns */}
        {DAYS.map((_, dayIdx) => {
          const dayWindows = localWindows.filter(w => w.day_of_week === dayIdx);
          const dayBlocks = localBlocks.filter(b => b.day_of_week === dayIdx);
          return (
            <div
              key={dayIdx}
              className="avail-day-col"
              ref={el => columnRefs.current[dayIdx] = el}
              onMouseDown={(e) => handleMouseDown(e, dayIdx)}
            >
              {/* Grid lines */}
              {TIME_LABELS.map((t, i) => (
                <div key={i} className={`avail-slot ${t.isHalf ? 'half' : 'hour'}`} />
              ))}

              {/* Existing windows */}
              {dayWindows.map(w => {
                const isResizingThis = resizing?.window.id === w.id;
                const isMovingThis = moving?.isDragging && moving.window.id === w.id;
                const isMovingToOtherDay = isMovingThis && moving!.currentDay !== dayIdx;

                // If this block is being moved to another day, don't render it here
                if (isMovingToOtherDay) return null;

                const displayStart = isResizingThis ? resizing!.currentStart : isMovingThis ? moving!.currentStart : w.localStart;
                const displayEnd = isResizingThis ? resizing!.currentEnd : isMovingThis ? moving!.currentStart + moving!.duration : w.localEnd;
                const top = (displayStart - START_HOUR) * PX_PER_HOUR;
                const height = (displayEnd - displayStart) * PX_PER_HOUR;
                const svcLabel = w.services.length > 0
                  ? w.services.map(id => services.find(s => s.id === id)?.name || id).join(', ')
                  : 'All services';
                return (
                  <div
                    key={w.id}
                    className={`avail-window-block ${isResizingThis ? 'resizing' : ''} ${isMovingThis ? 'moving' : ''}`}
                    style={{ top: `${top}px`, height: `${height}px` }}
                    onClick={(e) => { e.stopPropagation(); if (!resizing && !moving) handleWindowClick(w); }}
                    onMouseDown={(e) => { if (!resizing) handleBlockMouseDown(e, w); }}
                    title={`${formatLocalTime(displayStart)} - ${formatLocalTime(displayEnd)}\n${svcLabel}`}
                  >
                    <div className="avail-resize-handle top" onMouseDown={(e) => handleResizeStart(e, w, 'top')} />
                    <div className="avail-block-time">{formatLocalTime(displayStart)} - {formatLocalTime(displayEnd)}</div>
                    <div className="avail-block-services">{svcLabel}</div>
                    <div className="avail-resize-handle bottom" onMouseDown={(e) => handleResizeStart(e, w, 'bottom')} />
                  </div>
                );
              })}

              {/* Blocked time (red) */}
              {dayBlocks.map(b => {
                const isBlockResizing = blockResizing?.block.id === b.id;
                const isBlockMoving = blockMoving?.isDragging && blockMoving.block.id === b.id;
                const isBlockMovingToOther = isBlockMoving && blockMoving!.currentDay !== dayIdx;
                if (isBlockMovingToOther) return null;

                const displayStart = isBlockResizing ? blockResizing!.currentStart : isBlockMoving ? blockMoving!.currentStart : b.localStart;
                const displayEnd = isBlockResizing ? blockResizing!.currentEnd : isBlockMoving ? blockMoving!.currentStart + blockMoving!.duration : b.localEnd;
                const top = (displayStart - START_HOUR) * PX_PER_HOUR;
                const height = (displayEnd - displayStart) * PX_PER_HOUR;
                return (
                  <div
                    key={b.id}
                    className={`avail-block-entry ${isBlockResizing ? 'resizing' : ''} ${isBlockMoving ? 'moving' : ''}`}
                    style={{ top: `${top}px`, height: `${height}px` }}
                    onClick={(e) => { e.stopPropagation(); if (!blockResizing && !blockMoving) handleBlockClick(b); }}
                    onMouseDown={(e) => { if (!blockResizing) handleBlockEntryMouseDown(e, b); }}
                    title={`BLOCKED: ${formatLocalTime(displayStart)} - ${formatLocalTime(displayEnd)}${b.reason ? `\n${b.reason}` : ''}`}
                  >
                    <div className="avail-resize-handle top" onMouseDown={(e) => handleBlockResizeStart(e, b, 'top')} />
                    <div className="avail-block-time">{formatLocalTime(displayStart)} - {formatLocalTime(displayEnd)}</div>
                    {b.reason && <div className="avail-block-services">{b.reason}</div>}
                    <div className="avail-resize-handle bottom" onMouseDown={(e) => handleBlockResizeStart(e, b, 'bottom')} />
                  </div>
                );
              })}

              {/* Render moving block in target day column */}
              {blockMoving?.isDragging && blockMoving.currentDay === dayIdx && blockMoving.block.day_of_week !== dayIdx && (
                <div
                  className="avail-block-entry moving"
                  style={{
                    top: `${(blockMoving.currentStart - START_HOUR) * PX_PER_HOUR}px`,
                    height: `${blockMoving.duration * PX_PER_HOUR}px`
                  }}
                >
                  <div className="avail-block-time">{formatLocalTime(blockMoving.currentStart)} - {formatLocalTime(blockMoving.currentStart + blockMoving.duration)}</div>
                </div>
              )}

              {/* Render moving block in target day column */}
              {moving?.isDragging && moving.currentDay === dayIdx && moving.window.day_of_week !== dayIdx && (
                <div
                  className="avail-window-block moving"
                  style={{
                    top: `${(moving.currentStart - START_HOUR) * PX_PER_HOUR}px`,
                    height: `${moving.duration * PX_PER_HOUR}px`
                  }}
                >
                  <div className="avail-block-time">{formatLocalTime(moving.currentStart)} - {formatLocalTime(moving.currentStart + moving.duration)}</div>
                </div>
              )}

              {/* Drag-to-create preview */}
              {creating && creating.day === dayIdx && Math.abs(creating.currentHour - creating.startHour) > 0.1 && (() => {
                const previewStart = Math.min(creating.startHour, creating.currentHour);
                const previewEnd = Math.max(creating.startHour, creating.currentHour);
                return (
                  <div
                    className="avail-create-preview"
                    style={{
                      top: `${(previewStart - START_HOUR) * PX_PER_HOUR}px`,
                      height: `${(previewEnd - previewStart) * PX_PER_HOUR}px`
                    }}
                  >
                    <span>{formatLocalTime(previewStart)} - {formatLocalTime(previewEnd)}</span>
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>

      {/* Edit/Create Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={
          modalMode === 'block'
            ? (editingBlock ? 'Edit Blocked Time' : 'New Blocked Time')
            : (editingWindow ? 'Edit Availability Window' : 'New Availability Window')
        }
        size="small"
        footer={
          <>
            {(editingWindow || editingBlock) && (
              <button className="btn btn-danger" onClick={handleDelete} style={{ marginRight: 'auto' }}>Delete</button>
            )}
            <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
            <button
              className={`btn ${modalMode === 'block' ? 'btn-danger' : 'btn-primary'}`}
              onClick={handleSave}
              disabled={modalForm.days.length === 0}
            >
              {(editingWindow || editingBlock) ? 'Update' : 'Create'}
            </button>
          </>
        }
      >
        <div className="avail-modal-form">
          {error && <div className="avail-modal-error">{error}</div>}

          {/* Mode toggle — only show when creating new (not editing) */}
          {!editingWindow && !editingBlock && (
            <div className="avail-mode-toggle">
              <button
                type="button"
                className={`mode-btn ${modalMode === 'availability' ? 'active availability' : ''}`}
                onClick={() => setModalMode('availability')}
              >
                Available
              </button>
              <button
                type="button"
                className={`mode-btn ${modalMode === 'block' ? 'active block' : ''}`}
                onClick={() => setModalMode('block')}
              >
                Blocked
              </button>
            </div>
          )}

          <div className="form-group">
            <label>{(editingWindow || editingBlock) ? 'Day' : 'Days'}</label>
            {editingWindow ? (
              <select value={modalForm.days[0]} onChange={e => setModalForm(prev => ({ ...prev, days: [parseInt(e.target.value)] }))}>
                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            ) : (
              <>
                <div className="avail-day-checks">
                  {DAYS.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`day-check ${modalForm.days.includes(i) ? 'active' : ''}`}
                      onClick={() => setModalForm(prev => ({
                        ...prev,
                        days: prev.days.includes(i) ? prev.days.filter(d => d !== i) : [...prev.days, i].sort()
                      }))}
                    >
                      {DAY_ABBR[i]}
                    </button>
                  ))}
                </div>
                <div className="avail-day-shortcuts">
                  <button type="button" onClick={() => setModalForm(prev => ({ ...prev, days: [1,2,3,4,5] }))}>Weekdays</button>
                  <button type="button" onClick={() => setModalForm(prev => ({ ...prev, days: [0,1,2,3,4,5,6] }))}>Every Day</button>
                  <button type="button" onClick={() => setModalForm(prev => ({ ...prev, days: [] }))}>Clear</button>
                </div>
              </>
            )}
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label>Start Time</label>
              <select value={modalForm.startHour} onChange={e => setModalForm(prev => ({ ...prev, startHour: parseFloat(e.target.value) }))}>
                {Array.from({ length: (END_HOUR - START_HOUR) * 4 }, (_, i) => {
                  const h = START_HOUR + i * 0.25;
                  return <option key={h} value={h}>{formatLocalTime(h)}</option>;
                })}
              </select>
            </div>
            <div className="form-group">
              <label>End Time</label>
              <select value={modalForm.endHour} onChange={e => setModalForm(prev => ({ ...prev, endHour: parseFloat(e.target.value) }))}>
                {Array.from({ length: (END_HOUR - START_HOUR) * 4 }, (_, i) => {
                  const h = START_HOUR + i * 0.25;
                  return h > modalForm.startHour ? <option key={h} value={h}>{formatLocalTime(h)}</option> : null;
                })}
              </select>
            </div>
          </div>

          {modalMode === 'availability' ? (
            <div className="form-group">
              <label>Services</label>
              <div className="avail-svc-toggles">
                {services.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    className={`svc-toggle ${modalForm.services.includes(s.id) ? 'active' : ''}`}
                    onClick={() => toggleService(s.id)}
                  >
                    {s.name}
                  </button>
                ))}
                {services.length === 0 && <span className="no-svc">No services defined</span>}
              </div>
              {modalForm.services.length === 0 && <span className="svc-hint">No services selected = available for all</span>}
            </div>
          ) : (
            <div className="form-group">
              <label>Reason (optional)</label>
              <input
                type="text"
                value={modalForm.reason}
                onChange={e => setModalForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="e.g., Lunch break, Focus time"
                style={{ width: '100%', padding: '6px 10px', borderRadius: 'var(--radius-small)', border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '13px' }}
              />
            </div>
          )}
        </div>
      </Modal>

      <style>{`
        .avail-grid-container {
          display: flex;
          flex-direction: column;
          height: calc(100vh - 280px);
          min-height: 500px;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-large);
          overflow: hidden;
          background: var(--bg-primary);
        }

        .avail-grid-header {
          display: flex;
          border-bottom: 2px solid var(--border-secondary);
          flex-shrink: 0;
        }
        .avail-gutter-header { width: 60px; min-width: 60px; }
        .avail-day-header {
          flex: 1;
          text-align: center;
          padding: 8px;
          font-weight: 600;
          font-size: 13px;
          color: var(--text-primary);
          border-left: 1px solid var(--border-secondary);
        }

        .avail-grid {
          flex: 1;
          display: flex;
          overflow-y: auto;
        }

        .avail-gutter {
          width: 60px;
          min-width: 60px;
          border-right: 1px solid var(--border-secondary);
          background: var(--bg-secondary);
          min-height: ${TOTAL_HOURS * PX_PER_HOUR}px;
        }
        .avail-time-label {
          height: ${PX_PER_HOUR / 2}px;
          display: flex;
          align-items: flex-start;
          justify-content: flex-end;
          padding: 0 6px;
          font-size: 10px;
          color: var(--text-secondary);
          transform: translateY(-6px);
        }
        .avail-time-label.half {
          font-size: 9px;
          color: var(--text-tertiary);
        }

        .avail-day-col {
          flex: 1;
          position: relative;
          border-left: 1px solid var(--border-secondary);
          cursor: crosshair;
          min-height: ${TOTAL_HOURS * PX_PER_HOUR}px;
        }

        .avail-slot {
          height: ${PX_PER_HOUR / 2}px;
          border-bottom: 1px solid color-mix(in srgb, var(--border-secondary) 40%, transparent);
        }
        .avail-slot.half {
          border-bottom: 1px solid var(--border-secondary);
        }

        .avail-window-block {
          position: absolute;
          left: 2px;
          right: 2px;
          background: repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 3px,
            rgba(16, 185, 129, 0.12) 3px,
            rgba(16, 185, 129, 0.12) 6px
          );
          border: 1px solid rgba(16, 185, 129, 0.4);
          border-left: 3px solid #10b981;
          border-radius: 4px;
          cursor: pointer;
          overflow: hidden;
          padding: 4px 6px;
          transition: box-shadow 0.15s;
          z-index: 2;
        }
        .avail-window-block:hover {
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
          background: repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 3px,
            rgba(16, 185, 129, 0.2) 3px,
            rgba(16, 185, 129, 0.2) 6px
          );
        }
        .avail-block-time {
          font-size: 11px;
          font-weight: 600;
          color: #10b981;
        }
        .avail-block-services {
          font-size: 10px;
          color: rgba(16, 185, 129, 0.7);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .avail-resize-handle {
          position: absolute;
          left: 0;
          right: 0;
          height: 6px;
          cursor: ns-resize;
          opacity: 0;
          transition: opacity 0.15s;
          z-index: 5;
        }
        .avail-window-block:hover .avail-resize-handle { opacity: 1; }
        .avail-resize-handle.top {
          top: 0;
          background: linear-gradient(to bottom, #10b981 0%, transparent 100%);
          border-radius: 4px 4px 0 0;
        }
        .avail-resize-handle.bottom {
          bottom: 0;
          background: linear-gradient(to top, #10b981 0%, transparent 100%);
          border-radius: 0 0 4px 4px;
        }
        .avail-window-block.resizing {
          opacity: 0.8;
          z-index: 10;
        }
        .avail-window-block.moving {
          opacity: 0.7;
          z-index: 10;
          cursor: grabbing;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .avail-create-preview {
          position: absolute;
          left: 2px;
          right: 2px;
          background: rgba(59, 130, 246, 0.15);
          border: 2px dashed rgba(59, 130, 246, 0.5);
          border-radius: 4px;
          z-index: 3;
          pointer-events: none;
          display: flex;
          align-items: flex-start;
          padding: 4px 6px;
          font-size: 11px;
          color: #3b82f6;
          font-weight: 500;
        }

        /* Modal form */
        .avail-modal-form { display: flex; flex-direction: column; gap: 14px; }
        .avail-modal-error {
          background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3);
          padding: 8px 12px; border-radius: 6px; color: #ef4444; font-size: 13px;
        }
        .form-group { display: flex; flex-direction: column; gap: 4px; }
        .form-group label { font-size: 13px; font-weight: 600; color: var(--text-secondary); }
        .form-group select {
          padding: 8px 12px; border: 1px solid var(--border-secondary); border-radius: var(--radius-medium);
          background: var(--bg-secondary); color: var(--text-primary); font-size: 14px;
        }
        .form-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .avail-day-checks { display: flex; gap: 6px; }
        .day-check {
          width: 40px; height: 36px; border-radius: 6px;
          border: 1px solid var(--border-secondary); background: var(--bg-secondary);
          color: var(--text-secondary); font-size: 12px; font-weight: 600;
          cursor: pointer; transition: all 0.15s;
        }
        .day-check.active { border-color: var(--accent-blue); background: rgba(59, 130, 246, 0.15); color: var(--accent-blue); }
        .avail-day-shortcuts { display: flex; gap: 8px; margin-top: 6px; }
        .avail-day-shortcuts button {
          background: none; border: none; color: var(--text-tertiary);
          font-size: 11px; cursor: pointer; text-decoration: underline; padding: 0;
        }
        .avail-day-shortcuts button:hover { color: var(--text-primary); }
        .avail-svc-toggles { display: flex; flex-wrap: wrap; gap: 6px; }
        .svc-toggle {
          padding: 4px 12px; border: 1px solid var(--border-secondary); border-radius: 14px;
          background: var(--bg-secondary); color: var(--text-secondary); font-size: 12px;
          cursor: pointer; transition: all 0.15s;
        }
        .svc-toggle.active { border-color: var(--accent-blue); background: rgba(59, 130, 246, 0.1); color: var(--accent-blue); }
        .no-svc { font-size: 12px; color: var(--text-tertiary); font-style: italic; }
        .svc-hint { font-size: 11px; color: var(--text-tertiary); margin-top: 2px; }

        /* Blocked time entries (red) */
        .avail-block-entry {
          position: absolute;
          left: 2px;
          right: 2px;
          background: repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 3px,
            rgba(239, 68, 68, 0.12) 3px,
            rgba(239, 68, 68, 0.12) 6px
          );
          border: 1px solid rgba(239, 68, 68, 0.4);
          border-left: 3px solid #ef4444;
          border-radius: 4px;
          cursor: pointer;
          overflow: hidden;
          padding: 4px 6px;
          transition: box-shadow 0.15s;
          z-index: 2;
        }
        .avail-block-entry:hover {
          box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
          background: repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 3px,
            rgba(239, 68, 68, 0.2) 3px,
            rgba(239, 68, 68, 0.2) 6px
          );
        }
        .avail-block-entry .avail-block-time { color: #ef4444; }
        .avail-block-entry .avail-block-services { color: rgba(239, 68, 68, 0.7); }
        .avail-block-entry .avail-resize-handle.top {
          background: linear-gradient(to bottom, #ef4444 0%, transparent 100%);
        }
        .avail-block-entry .avail-resize-handle.bottom {
          background: linear-gradient(to top, #ef4444 0%, transparent 100%);
        }
        .avail-block-entry:hover .avail-resize-handle { opacity: 1; }

        /* Mode toggle */
        .avail-mode-toggle {
          display: flex; gap: 0; border-radius: var(--radius-medium); overflow: hidden;
          border: 1px solid var(--border-secondary);
        }
        .mode-btn {
          flex: 1; padding: 8px 16px; border: none; cursor: pointer;
          font-size: 13px; font-weight: 600; transition: all 0.15s;
          background: var(--bg-secondary); color: var(--text-tertiary);
        }
        .mode-btn:first-child { border-right: 1px solid var(--border-secondary); }
        .mode-btn.active.availability { background: rgba(16, 185, 129, 0.15); color: #10b981; }
        .mode-btn.active.block { background: rgba(239, 68, 68, 0.15); color: #ef4444; }
        .mode-btn:hover:not(.active) { background: var(--bg-tertiary); color: var(--text-secondary); }

        .btn { padding: 8px 16px; border-radius: var(--radius-medium); font-size: 14px; font-weight: 500; cursor: pointer; border: none; transition: all 0.15s; display: inline-flex; align-items: center; gap: 6px; }
        .btn-primary { background: var(--accent-blue); color: white; }
        .btn-primary:hover { opacity: 0.9; }
        .btn-secondary { background: var(--bg-secondary); color: var(--text-secondary); border: 1px solid var(--border-secondary); }
        .btn-danger { background: #ef4444; color: white; }
        .btn-danger:hover { opacity: 0.9; }
      `}</style>
    </div>
  );
}
